import _ from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import BookingField from './BookingDrawerField.react';
import Currencies from 'arbitrip-common/general/utils/Currencies';
import { ReactComponent as OutOfPolicyIcon } from '../../../img/results_page/check_brown@3x.svg';
import { ReactComponent as InPolicyIcon } from '../../../img/results_page/check_green@3x.svg';
import { useTranslation } from 'react-i18next';
import { getIsBnpl } from '../../../utils/PayLater';

const date_format = 'D MMM YYYY';

const BookingDrawerReservationSummary = ({ booking, totalPrice, isElal }) => {
    const { t } = useTranslation();

    if (!booking) {
        return null;
    }

    const checkIn = useMemo(() => {
        const date = _.get(booking, 'deal.details.check_in');
        if (moment.isMoment(date)) {
            return date.format(date_format);
        }
    }, [booking]);

    const checkOut = useMemo(() => {
        const date = _.get(booking, 'deal.details.check_out');
        if (moment.isMoment(date)) {
            return date.format(date_format);
        }
    }, [booking]);

    const traveler = useMemo(() => {
        return _.get(booking, 'travelers', []).length + (_.get(booking, 'deal.details.children_count', 0) * _.get(booking, 'deal.details.room_count', 1));
    }, [booking]);

    const room = useMemo(() => {
        return _.get(booking, 'deal.details.room_count');
    }, [booking]);

    const nights = useMemo(() => {
        return _.get(booking, 'deal.details.nights');
    }, [booking]);

    const isBnpl = useMemo(() => getIsBnpl(booking), [booking]);

    const usdPriceAfterDiscounts = useMemo(() => {
        const exchangeRate = booking?.deal?.cheapopoPricing?.outputCurrencyToUsdExchangeRate || 1;

        const finalPriceInDealCurrency = booking?.payment?.sum ?? 0;
        return finalPriceInDealCurrency * exchangeRate;
    }, [booking]);

    const bnplUsdAmount = useMemo(() => {
        if (!isBnpl || !isElal) return null;
        const raw = booking?.payment?.bnpl?.usd_amount_with_fee;
        return raw > 0 ? Math.round(raw) : null;
    }, [booking, isBnpl, isElal]);

    const price = useMemo(() => {
        if (totalPrice) {
            return Currencies.getPriceWithCurrency(totalPrice, booking?.deal?.cheapopoPricing?.currency);
        }

        if (isElal) {
            const loyaltyPoints = booking.payment?.loyalty?.points ?? 0;
            if (isBnpl && bnplUsdAmount != null) {
                const pointsStr = loyaltyPoints > 0 ? `${loyaltyPoints.toLocaleString('en-US')} ${t('ShortPoints')} + ` : '';
                return `${pointsStr}$${bnplUsdAmount}`;
            }
            const paidPrice = booking?.payment?.sum ?? 0;
            const priceString = Currencies.getPriceWithCurrency(paidPrice, booking.deal.cheapopoPricing.currency);
            return loyaltyPoints > 0
                ? `${priceString} + ${loyaltyPoints.toLocaleString('en-US')} ${t('ShortPoints')}`
                : priceString;
        }

        return Currencies.getPriceWithCurrency(
            booking?.deal?.cheapopoPricing?.cheapopoTotalPrice || 0,
            booking?.deal?.cheapopoPricing?.currency,
        );
    }, [booking, bnplUsdAmount]);

    const getInPolicy = () => {
        return totalPrice ? null : (
            <div className="policy">
                {_.get(booking, 'deal.in_policy') ? (
                    <div className="in_policy">
                        <InPolicyIcon />
                        {t('InPolicy')}
                    </div>
                ) : (
                    <div className="out_of_policy">
                        <OutOfPolicyIcon />
                        {t('OutOfPolicy')}
                    </div>
                )}
            </div>
        );
    };

    const commission = useMemo(() => Number(_.get(booking, 'deal.cheapopoPricing.commissionTotal', 0)), [booking]);

    const hasTravelPolicy = useMemo(() => _.get(booking, 'deal', {}).hasOwnProperty('in_policy'), [booking]);

    const getCommission = () => {
        const currency = _.get(booking, 'deal.cheapopoPricing.currency');

        return (
            <div className="commission">
                {Currencies.getPriceWithDisplayCurrencyByPrecision(commission, 1, currency, 2)}
            </div>
        );
    };

    const getPayAtTheHotel = () => {
        const currency = _.get(booking, 'deal.cheapopoPricing.payments_excluded_currency');

        return (
            <div className="pay-at-hotel">
                {Currencies.getPriceWithDisplayCurrencyByPrecision(payments_excluded, 1, currency, 2)}
            </div>
        );
    };

    const payments_excluded = useMemo(
        () => Number(_.get(booking, 'deal.cheapopoPricing.payments_excluded', 0)),
        [booking],
    );

    const chargeDate = useMemo(() => {
        const date = _.get(booking, 'payment.charge_date');
        return date ? moment.utc(date).format(date_format) : null;
    }, [booking]);

    return (
        <BookingField
            fieldTitle={t('ReservationSummary')}
            fieldValue={
                <div className="res-summary-fields">
                    {/* Check-in / Check-out */}
                    <div className="res-summary-row">
                        <div className="res-summary-col">
                            <span className="res-summary-label">{t('CheckIn')}</span>
                            <span className="res-summary-value" data-testid="booking-drawer-check-in">{checkIn}</span>
                        </div>
                        <div className="res-summary-col">
                            <span className="res-summary-label">{t('CheckOut')}</span>
                            <span className="res-summary-value" data-testid="booking-drawer-check-out">{checkOut}</span>
                        </div>
                    </div>

                    {/* Traveler / Room / Nights */}
                    <div className="res-summary-row">
                        <div className="res-summary-col">
                            <span className="res-summary-label">{traveler === 1 ? t('Traveler') : t('Travelers')}</span>
                            <span className="res-summary-value" data-testid="booking-drawer-guests-info">{traveler}</span>
                        </div>
                        <div className="res-summary-col">
                            <span className="res-summary-label">{room === 1 ? t('Room') : t('Rooms')}</span>
                            <span className="res-summary-value">{room}</span>
                        </div>
                        <div className="res-summary-col">
                            <span className="res-summary-label">{nights === 1 ? t('Night') : t('Nights')}</span>
                            <span className="res-summary-value">{nights}</span>
                        </div>
                    </div>

                    {/* Total Price + Commission + Policy */}
                    <div className="res-summary-row">
                        <div className="res-summary-col">
                            <span className="res-summary-label">{t('TotalPrice')}</span>
                            <span className="res-summary-value">{price}</span>
                        </div>
                        {commission > 0 && (
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('Commission')}</span>
                                <span className="res-summary-value">{getCommission()}</span>
                            </div>
                        )}
                        {booking.travel_policy && (
                            <div className="res-summary-col">
                                <span className="res-summary-label">&nbsp;</span>
                                <span className="res-summary-value">{getInPolicy()}</span>
                            </div>
                        )}
                    </div>

                    {/* Coupon */}
                    {booking?.payment?.coupon?.code && (
                        <div className="res-summary-row">
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('CouponCode')}</span>
                                <span className="res-summary-value">{booking.payment.coupon.code}</span>
                            </div>
                        </div>
                    )}

                    {/* Price in USD as shown before payment — non-BNPL ElAl */}
                    {isElal && !isBnpl && (
                        <div className="res-summary-row">
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('PriceInUsdAsShown')}</span>
                                <span className="res-summary-value">{Currencies.getPriceWithCurrency(usdPriceAfterDiscounts, 'USD')}</span>
                            </div>
                        </div>
                    )}

                    {/* Price in USD as shown before payment — BNPL ElAl (fee-inclusive amount) */}
                    {isElal && isBnpl && bnplUsdAmount != null && (
                        <div className="res-summary-row">
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('PriceInUsdAsShown')}</span>
                                <span className="res-summary-value">{Currencies.getPriceWithCurrency(bnplUsdAmount, 'USD')}</span>
                            </div>
                        </div>
                    )}

                    {/* Scheduled charge date */}
                    {isBnpl && chargeDate && (
                        <div className="res-summary-row">
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('BnplScheduledChargeDate')}</span>
                                <span className="res-summary-value">{t('BnplScheduledChargeDateValue', { date: chargeDate })}</span>
                            </div>
                        </div>
                    )}

                    {/* Pay at hotel */}
                    {payments_excluded > 0 && !totalPrice && (
                        <div className="res-summary-row">
                            <div className="res-summary-col">
                                <span className="res-summary-label">{t('PayAtHotel')}</span>
                                <span className="res-summary-value">{getPayAtTheHotel()}</span>
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default BookingDrawerReservationSummary;
